<template>
  <div class="work-order-form">
      <form @submit.stop.prevent="form.handleSubmit()">
        <!-- projects and workorder id -->
        <div class="grid grid-cols-2 gap-4">

          <BaseInput
            :model-value="project.name"
            label="Project"
            type="text"
            :disabled="true"
          />


          <BaseInput
            :model-value="workOrder.workOrderId"
            type="text"
            label="Work Order #"
            :disabled="true"
          />
        </div>

        <!-- subcontractor and wo date -->
        <div class="grid grid-cols-2 gap-4 mt-4">
          <form.Field
            name="subId"
            :validators="{
              onBlur: yup.string().required('Subcontractor is required'),
            }"
          >
            <template #default="{ field }">
              <BaseSelect
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Subcontractor"
                :options="subcontractors"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>

          <BaseInputDate
            :model-value="(workOrder.date as any)"
            label="Work Order Date"
            :disabled="true"
          />
        </div>

        <h4 class="mt-6">Items</h4>
        <hr class="mt-2">
        <!-- items -->
        <div>
          <form.Field
            name="items"
            :validators="{
              onChange: yup.array().min(1, 'At least one item is required')
            }"
          >
            <template #default="{ field }">
              <div
                v-if="(field.state.meta.errors && field.state.meta.errors[0]) && form.getFieldValue('items').length === 0"
                class="text-base/6 text-error sm:text-sm/6 mt-2 flex flex-col items-center"
              >
                <span class="i-heroicons-exclaimation-circle size-8" />
                <p class="text-base/6 text-error sm:text-sm/6">
                  {{ field.state.meta.errors[0] }}
                </p>
              </div>

              <div
                v-for="(_, i) of field.state.value"
                :key="i"
              >
                <div class="flex justify-end mt-8">
                  <span
                    class="i-heroicons-x-circle cursor-pointer hover:text-error h-[1.2rem] w-[1.2rem]"
                    @click="removeItem(field, Number(i))"
                  />
                </div>

                <!-- Title and Cost -->
                <div class="grid grid-cols-2 gap-4">
                  <form.Field
                    :key="`name-${i}`"
                    :name="( `items[${i}].name` as any)"
                    :validators="{
                      onBlur: yup.string().required('Title is required'),
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInput
                        :model-value="(subField.state.value as string)"
                        type="text"
                        label="Title"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => typeof e === 'string' ? subField.handleChange(e) : subField.handleChange(e.target.value)"
                      />
                    </template>
                  </form.Field>

                  <form.Field
                    :key="`cost-${i}`"
                    :name="(`items[${i}].cost` as any)"
                    :validators="{
                      onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Price must be a valid number',(value) => !isNaN(Number(value))).required('Price is required')
                    }"
                  >
                    <template #default="{ field: subField }">
                      <BaseInputCurrency
                        :model-value="(subField.state.value as string)"
                        label="Price"
                        :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                        @blur="subField.handleBlur"
                        @input="(e) => {
                          subField.handleChange(e.target.value);
                          setTotal()
                        }"
                      />
                    </template>
                  </form.Field>
                </div>

                <!-- Scope of Work -->
                <div class="mt-4">
                  <form.Field
                    :key="`scopeOfWork-${i}`"
                    :name="(`items[${i}].scopeOfWork` as any)"
                  >
                    <template #default="{ field: subField }">
                      <label class="select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium">Scope of Work</label>

                      <div class="mt-2">
                        <Editor
                          :model-value="(subField.state.value as string)"
                          @blur="subField.handleBlur"
                          @update:model-value="(value) => subField.handleChange(value)"
                        />
                      </div>
                    </template>
                  </form.Field>
                </div>
              </div>

              <div class="my-5">
                <BaseButton
                  variant="secondary"
                  type="button"
                  text="Add item"
                  @click="addItem(field)"
                />
              </div>
            </template>
          </form.Field>
        </div>

        <div class="mt-5 w-fit ml-auto">
          <div class="flex items-baseline justify-end bg-slate-100 px-4 pt-4 rounded-lg gap-4">
            <span class="text-slate-700">Total</span>
            <form.Field
              name="total"
              :validators="{
                onBlur: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Total must be a valid number',(value) => !isNaN(Number(value))).required('Total is required')
              }"
            >
              <template #default="{ field }">
                <BaseInputCurrency
                  :model-value="(field.state.value as string)"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  @blur="field.handleBlur"
                  @input="(e) => field.handleChange(e.target.value)"
                />
              </template>
            </form.Field>
          </div>
        </div>

        <hr class="mt-6">

        <div class="flex justify-end gap-2 mt-3">
          <form.Subscribe>
            <template #default="{ canSubmit, isSubmitting }">
              <BaseButton
                variant="base"
                text="Cancel"
                :to="{ name: 'ShowWorkOrder', params: { id: workOrder.id } }"
                :disabled="isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
              />

              <BaseButton
                variant="primary"
                text="Save"
                type="submit"
                :disabled="!canSubmit || isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
              />
            </template>
          </form.Subscribe>
        </div>
      </form>

  </div>
</template>

<script lang="ts" setup>
import { WorkOrder, WorkOrderItem } from '@/models/workOrder'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseInputCurrency from '@/components/base/BaseInputCurrency.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import Editor from '@/components/TextEditor.vue'
import { difference } from 'underscore'
import { PropType, ref } from 'vue'
import { Project } from '@/models/project'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { decimalFormat, toNumber } from '@/utils'

const props = defineProps({
  model: {
    type: Object as PropType<WorkOrder>,
    required: true
  },
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  subcontractors: {
    type: Array as PropType<{ value: string, label: string }[]>,
    required: true
  },
  saveFunction: {
    type: Function as PropType<Function>,
    required: true
  }
})

const workOrder = ref({...props.model})

const form = useForm({
  defaultValues: workOrder.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const workOrder = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as unknown as WorkOrder
    workOrder.items = workOrder.items.map((item) => {
      const woItem = { ...item, cost: toNumber(item.cost) }
      return Object.fromEntries(Object.entries(woItem).filter(([, v]) => v !== undefined)) as unknown as WorkOrderItem
    })
    workOrder.total = toNumber(workOrder.total)

    await props.saveFunction(workOrder)
  },
})


const setTotal = () => {
  const total = form.getFieldValue('items').reduce((acc, item) => {
    return acc + toNumber(item.cost)
  }, 0)

  if (total) {
    form.setFieldValue('total', decimalFormat(total, 2) as any)
  } else {
    form.setFieldValue('total', String(0) as any)
  }
}

const addItem = (field) => {
  field.pushValue({
    ...new WorkOrderItem(),
    cost: '0' as any
  } as WorkOrderItem)
}

const removeItem = (field, index) => {
  const woItem = { ...form.getFieldValue('items')[index] }
  const woItemlineItems = woItem.lineItems

  if (woItemlineItems.length) {
    form.setFieldValue('lineItems', difference(form.getFieldValue('lineItems'), woItemlineItems))
  }
  field.removeValue(index)
  setTotal()
}

</script>
